import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";

const IDEAS = [
    {
        n: "1",
        title: "The referee actually thinks - it doesn't read from a script",
        body: "A bank asks to transfer a bond. Our engine checks the request against a rulebook, live: is the right agent in charge? Is the buyer allowed? Is every required field filled in? In our star scenario one required field is missing, so the engine puts the transfer into FREEZE. Nobody typed “FREEZE” anywhere in advance - the engine found the problem itself.",
        note: "The missing field mirrors the structured-data rules that become mandatory on Swift's network in November 2026 - a real deadline every bank is preparing for.",
        cta: { label: "Watch the engine freeze a transfer, live", to: "/live" },
    },
    {
        n: "2",
        title: "Frozen doesn't mean dead - the right person fixes it",
        body: "The system automatically sends a repair request to the one organisation responsible for the missing information - like a teacher handing your essay back to you, not posting it on the wall. They supply the fix, the engine re-checks everything, and now it says RELEASE: good to go.",
    },
    {
        n: "3",
        title: "The “go” is a one-time ticket that's hard to abuse",
        body: "When the engine says yes, it issues a permission ticket - like a concert ticket with your name, seat, expiry time and a barcode. A separate “bouncer” (the execution adapter) checks the ticket itself before anything moves. We attack our own system to prove it: a forged ticket is rejected; an expired one is rejected; a ticket for a different transfer is rejected; and using a real ticket twice fails, because each ticket burns on use.",
        note: "31 automated conformance tests prove these rejections, every run.",
    },
    {
        n: "4",
        title: "Same referee, different rulebooks",
        body: "Swap the bond rulebook for a fund rulebook, or for a “bondholders vote on early repayment” scenario - the same engine runs all of them. That is the asset-agnostic part Swift asked for: one reusable referee, many games.",
        cta: { label: "See the corporate-action scenario", to: "/request/CA-ELEC-001" },
    },
    {
        n: "5",
        title: "Everyone agrees, without seeing your homework",
        body: "Each organisation involved digitally signs off on the state of things - “yes, the election is valid; yes, it was repaired; yes, it executed” - without handing over their private documents. The final receipt proves what happened without revealing whose holding it was or what was missing. Agreement on the outcome, privacy on the evidence.",
    },
];

export default function Explained() {
    return (
        <Layout>
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                Plain-English overview
            </p>
            <h1 className="mt-3 text-3xl font-medium tracking-tight text-slate-900">
                What this demo shows, in plain words
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                Banks are starting to move bonds and fund shares around as digital
                tokens - a bit like items in a game inventory, but worth millions.
                The hard question is: <span className="font-medium text-slate-800">who
                checks the rules before a token actually moves?</span> Swift, the
                network banks use to talk to each other, asked for a
                &ldquo;referee&rdquo; that works for any kind of token. That is what
                this sandbox demonstrates.
            </p>

            <div className="mt-10 space-y-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200">
                {IDEAS.map((idea) => (
                    <section key={idea.n} className="bg-white p-6 sm:p-8" data-testid={`explained-idea-${idea.n}`}>
                        <div className="flex items-baseline gap-4">
                            <span className="font-mono text-xl text-blue-950">{idea.n}</span>
                            <h2 className="text-lg font-medium tracking-tight text-slate-900">
                                {idea.title}
                            </h2>
                        </div>
                        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 sm:pl-9">
                            {idea.body}
                        </p>
                        {idea.note && (
                            <p className="mt-3 max-w-3xl rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-500 sm:ml-9">
                                {idea.note}
                            </p>
                        )}
                        {idea.cta && (
                            <p className="mt-4 sm:pl-9">
                                <Link
                                    to={idea.cta.to}
                                    className="text-sm font-medium text-blue-950 underline underline-offset-2 hover:text-blue-800"
                                >
                                    {idea.cta.label} →
                                </Link>
                            </p>
                        )}
                    </section>
                ))}
            </div>

            <section className="mt-10 rounded-sm border border-slate-200 bg-white p-8" data-testid="explained-summary">
                <h2 className="text-lg font-medium tracking-tight text-slate-900">
                    The whole demo in one sentence
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-700">
                    A smart referee that decides live, fixes problems through the right
                    people, hands out un-fakeable one-shot tickets, works for any
                    asset, and keeps everyone&rsquo;s secrets.
                </p>
                <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-400">
                    Everything in this sandbox uses synthetic market data - no real
                    assets, holdings or customer information. The governance, role
                    exchange, decision integrity and execution gating are real within
                    the prototype.
                </p>
            </section>
        </Layout>
    );
}
